import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BsCalendar2WeekFill } from 'react-icons/bs';
import { CgSpinner } from 'react-icons/cg';
import { toast } from 'react-toastify';

import { userAppData } from '../MainApp.js';
import RotaAddCoverLauncher from '../common/RotaAddCoverLauncher.js';
import { getDashboardRota } from './dashboardRotasApi.js';

import '../bookings/bookingsTables.css';
import './DashboardRotas.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };
const DAY_LABELS = {
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
  SUN: 'Sunday',
};
const DAY_ORDER = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

function coerceBool(value) {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }

  const normalisedValue = String(value || '').trim().toLowerCase();
  return ['true', '1', 'yes', 'y', 'on'].includes(normalisedValue);
}

function getOrderedDayCodes(weekView = {}, blocks = []) {
  const apiDayCodes = Array.isArray(weekView?.dayCodes) ? weekView.dayCodes : [];
  const blockDayCodes = blocks.map((block) => String(block?.dayOfWeek || '').trim().toUpperCase());
  const uniqueDayCodes = Array.from(new Set([...apiDayCodes, ...blockDayCodes].filter(Boolean)));

  return DAY_ORDER.filter((dayCode) => uniqueDayCodes.includes(dayCode));
}

function buildRowData(blocks = []) {
  const groupedRows = new Map();

  blocks.forEach((block) => {
    const rowKey = `${block.period || ''}|${block.start || ''}|${block.end || ''}`;
    if (!groupedRows.has(rowKey)) {
      groupedRows.set(rowKey, {
        rowKey,
        period: block.period || '',
        start: block.start || '',
        end: block.end || '',
        cells: {},
      });
    }

    groupedRows.get(rowKey).cells[block.dayOfWeek] = block;
  });

  return Array.from(groupedRows.values());
}

function getCellText(block = {}) {
  if (block.displayName) {
    return block.displayName;
  }
  if (block.hasAssignment && !block.isVisible) {
    return 'Private';
  }
  return 'Unassigned';
}

function getCoveringText(block = {}) {
  const coveringDisplayName = String(block.coveringDisplayName || '').trim();
  if (!block.isOverride || !coveringDisplayName) {
    return '';
  }
  return `Covering: ${coveringDisplayName}`;
}

function MobileDayTables({
  orderedDayCodes = [],
  rowData = [],
  accessLevel = '',
  showCurrentSlotHighlight = false,
  currentDayCode = '',
}) {
  const dayCardRefs = useRef({});

  useEffect(() => {
    const targetDayCode = String(currentDayCode || '').trim().toUpperCase();
    if (!targetDayCode || !orderedDayCodes.includes(targetDayCode)) {
      return;
    }

    const targetNode = dayCardRefs.current[targetDayCode];
    if (!targetNode) {
      return;
    }

    targetNode.scrollIntoView({
      behavior: 'smooth',
      inline: 'start',
      block: 'nearest',
    });
  }, [currentDayCode, orderedDayCodes]);

  return (
    <div className='dashboardRotas_mobileDaysWrap'>
      {orderedDayCodes.map((dayCode) => (
        <section
          className='dashboardRotas_mobileDayCard'
          key={dayCode}
          ref={(node) => {
            if (node) {
              dayCardRefs.current[dayCode] = node;
            }
          }}
        >
          <div className='dashboardRotas_mobileDayHeader'>
            <h3>{DAY_LABELS[dayCode] || dayCode}</h3>
            {dayCode === currentDayCode && (
              <span className='dashboardRotas_mobileTodayPill'>Today</span>
            )}
          </div>
          <table className='bookingsTable dashboardRotas_mobileDayTable'>
            <thead>
              <tr className='bookingsTableHeader'>
                <th>Lesson</th>
                <th>Assignment</th>
              </tr>
            </thead>
            <tbody>
              {rowData.map((row) => {
                const block = row.cells[dayCode];
                return (
                  <tr className='bookingsTableRow' key={`${row.rowKey}-${dayCode}-mobile`}>
                    <td className='bookingsTableData'>
                      <div className='dashboardRotas_rowLabel'>
                        <span>{row.period || 'Block'}</span>
                        <small>{row.start} - {row.end}</small>
                      </div>
                    </td>
                    <td className='bookingsTableSingleRoomData'>
                      {block ? (
                        <div
                          className={`dashboardRotas_cellValue${block.isCurrentUser ? ' dashboardRotas_cellValue--mine' : ''}${showCurrentSlotHighlight && block.isCurrentSlot ? ' dashboardRotas_cellValue--active' : ''}`}
                        >
                          <span>{getCellText(block)}</span>
                          {getCoveringText(block) && (
                            <small className='dashboardRotas_coveringText'>{getCoveringText(block)}</small>
                          )}
                        </div>
                      ) : (
                        <div className='dashboardRotas_cellValue dashboardRotas_cellValue--empty'>Not used</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ))}
      <p className='dashboardRotas_helperText dashboardRotas_helperText--mobile'>
        {accessLevel === 'view_self_only'
          ? 'Only your own rota assignments are visible in this view.'
          : 'Your own assignments are highlighted in green and the current live slot is outlined.'}
      </p>
    </div>
  );
}

function DashboardRotaWeekGrid({
  weekView = {},
  accessLevel = '',
  showCurrentSlotHighlight = false,
  currentDayCode = '',
}) {
  const blocks = useMemo(
    () => (Array.isArray(weekView?.blocks) ? weekView.blocks : []),
    [weekView],
  );
  const orderedDayCodes = useMemo(() => getOrderedDayCodes(weekView, blocks), [weekView, blocks]);
  const isCustomGrid = orderedDayCodes.length === 0;
  const rowData = useMemo(() => buildRowData(blocks), [blocks]);

  if (blocks.length === 0) {
    return <p className='dashboardRotas_emptyText'>No rota blocks are available for this week.</p>;
  }

  if (isCustomGrid) {
    return (
      <div className='dashboardRotas_tableWrap'>
        <table className='bookingsTable dashboardRotas_table'>
          <thead>
            <tr className='bookingsTableHeader'>
              <th>Block</th>
              <th>Time</th>
              <th>Assignment</th>
            </tr>
          </thead>
          <tbody>
            {blocks.map((block) => (
              <tr className='bookingsTableRow' key={block.blockId}>
                <td className='bookingsTableData'>{block.period}</td>
                <td className='bookingsTableSingleRoomData'>{block.start} - {block.end}</td>
                <td className='bookingsTableSingleRoomData'>
                  <div
                    className={`dashboardRotas_cellValue${block.isCurrentUser ? ' dashboardRotas_cellValue--mine' : ''}${showCurrentSlotHighlight && block.isCurrentSlot ? ' dashboardRotas_cellValue--active' : ''}`}
                  >
                    <span>{getCellText(block)}</span>
                    {getCoveringText(block) && (
                      <small className='dashboardRotas_coveringText'>{getCoveringText(block)}</small>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className='dashboardRotas_helperText'>
          {accessLevel === 'view_self_only'
            ? 'Only your own rota assignments are visible in this view.'
            : 'Your own assignments are highlighted in green and the current live slot is outlined.'}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className='dashboardRotas_tableWrap dashboardRotas_tableWrap--desktop'>
        <table className='bookingsTable dashboardRotas_table'>
          <thead>
            <tr className='bookingsTableHeader'>
              <th>Lesson</th>
              {orderedDayCodes.map((dayCode) => (
                <th key={dayCode}>{DAY_LABELS[dayCode] || dayCode}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowData.map((row) => (
              <tr className='bookingsTableRow' key={row.rowKey}>
                <td className='bookingsTableData'>
                  <div className='dashboardRotas_rowLabel'>
                    <span>{row.period}</span>
                    <small>{row.start} - {row.end}</small>
                  </div>
                </td>
                {orderedDayCodes.map((dayCode) => {
                  const block = row.cells[dayCode];
                  return (
                    <td className='bookingsTableSingleRoomData' key={`${row.rowKey}-${dayCode}`}>
                      {block ? (
                        <div
                          className={`dashboardRotas_cellValue${block.isCurrentUser ? ' dashboardRotas_cellValue--mine' : ''}${showCurrentSlotHighlight && block.isCurrentSlot ? ' dashboardRotas_cellValue--active' : ''}`}
                        >
                          <span>{getCellText(block)}</span>
                          {getCoveringText(block) && (
                            <small className='dashboardRotas_coveringText'>{getCoveringText(block)}</small>
                          )}
                        </div>
                      ) : (
                        <div className='dashboardRotas_cellValue dashboardRotas_cellValue--empty'>Not used</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className='dashboardRotas_helperText'>
          {accessLevel === 'view_self_only'
            ? 'Only your own rota assignments are visible in this view.'
            : 'Your own assignments are highlighted in green and the current live slot is outlined.'}
        </p>
      </div>

      <div className='dashboardRotas_mobileOnly'>
        <MobileDayTables
          orderedDayCodes={orderedDayCodes}
          rowData={rowData}
          accessLevel={accessLevel}
          showCurrentSlotHighlight={showCurrentSlotHighlight}
          currentDayCode={currentDayCode}
        />
      </div>
    </>
  );
}

export default function DashboardRotas() {
  const dashboardRotas = Array.isArray(userAppData?.dashboardRotas) ? userAppData.dashboardRotas : [];
  const rotaAccessEntries = Array.isArray(userAppData?.rotasData?.access)
    ? userAppData.rotasData.access
    : [];
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [selectedWeekNumber, setSelectedWeekNumber] = useState(0);

  const selectedWeekView = useMemo(() => {
    const weekViews = Array.isArray(modalData?.weekViews) ? modalData.weekViews : [];
    return weekViews.find((weekView) => Number(weekView.weekNumber) === Number(selectedWeekNumber))
      || weekViews[0]
      || null;
  }, [modalData, selectedWeekNumber]);
  const showCurrentSlotHighlight = Number(selectedWeekNumber) === Number(modalData?.currentWeekNumber ?? 0);

  if (dashboardRotas.length === 0) {
    return null;
  }

  async function openRotaModal(rota) {
    const masterRotaId = String(rota?.masterRotaId || '').trim();
    if (!masterRotaId) {
      return;
    }

    setModalOpen(true);
    setLoading(true);
    setModalData(null);
    try {
      const response = await getDashboardRota(masterRotaId);
      setModalData(response);
      setSelectedWeekNumber(Number(response?.defaultWeekNumber ?? 0));
    } catch (error) {
      toast.error(`❌ Failed to load the rota "${rota?.rotaName || 'Unknown rota'}".`, TOAST_CONFIG);
      setModalOpen(false);
    } finally {
      setLoading(false);
    }
  }

  function closeModal() {
    setModalOpen(false);
    setLoading(false);
    setModalData(null);
    setSelectedWeekNumber(0);
  }

  function userCanAddCover(masterRotaId) {
    const cleanMasterRotaId = String(masterRotaId || '').trim();
    if (!cleanMasterRotaId) {
      return false;
    }

    const matchingEntry = rotaAccessEntries.find((entry) => (
      entry
      && typeof entry === 'object'
      && String(entry.MasterRotaId || '').trim() === cleanMasterRotaId
    ));

    return coerceBool(matchingEntry?.allowOverride);
  }
  return (
    <>
      <div className='dashboardRotas_card'>
        <h1 className='dashboard_tabHeaders'>Rotas</h1>
        {dashboardRotas.map((rota) => (
          <div className='dashboardRotas_buttonRow' key={rota.masterRotaId || rota.rotaName}>
            <button
              className='dashboardRotas_button'
              type='button'
              onClick={() => openRotaModal(rota)}
            >
              <BsCalendar2WeekFill color='#ffffff' size={42} />
              <p className='dashboardRotas_buttonText'>{rota.rotaName}</p>
            </button>

            {userCanAddCover(rota.masterRotaId) && (
              <RotaAddCoverLauncher
                rota={rota}
                triggerClassName='dashboardRotas_coverButton'
              >
                <>
                  <span className='dashboardRotas_coverButtonIcon'>+</span>
                  <span className='dashboardRotas_coverButtonText'>Add Cover</span>
                </>
              </RotaAddCoverLauncher>
            )}
          </div>
        ))}
      </div>

      {modalOpen && (
        <div className='dashboardRotas_modalOverlay' onClick={closeModal}>
          <div
            className='dashboardRotas_modal'
            role='dialog'
            aria-modal='true'
            onClick={(event) => event.stopPropagation()}
          >
            <div className='dashboardRotas_modalHeader'>
              <div className='dashboardRotas_modalHeaderText'>
                <h2 className='dashboardRotas_modalTitle'>{modalData?.rotaName || 'Loading rota'}</h2>
                {modalData && (
                  <>
                    <p className='dashboardRotas_modalSubtitle'>
                      Current Week {modalData.currentWeekNumber}
                    </p>
                    <p className='dashboardRotas_modalMetaText'>
                      {modalData.currentDayLabel} {modalData.currentDate} at {modalData.currentTime}
                    </p>
                  </>
                )}
              </div>
              <button
                className='dashboardRotas_closeButton'
                type='button'
                onClick={closeModal}
                aria-label='Close modal'
              >
                &times;
              </button>
            </div>

            {loading ? (
              <div className='dashboardRotas_loadingState'>
                <CgSpinner className='dashboardRotas_spinner' />
                <p>Loading rota...</p>
              </div>
            ) : modalData ? (
              <>
                {Array.isArray(modalData.weekViews) && modalData.weekViews.length > 1 && (
                  <div className='dashboardRotas_controls'>
                    <label htmlFor='dashboard-rotas-week-select'>Week</label>
                    <select
                      id='dashboard-rotas-week-select'
                      value={selectedWeekNumber}
                      onChange={(event) => setSelectedWeekNumber(Number(event.target.value))}
                    >
                      {modalData.weekViews.map((weekView) => (
                        <option key={weekView.weekNumber} value={weekView.weekNumber}>
                          {weekView.weekLabel}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedWeekView && (
                  <>
                    <p className='dashboardRotas_selectedWeek'>{selectedWeekView.weekLabel}</p>
                    <DashboardRotaWeekGrid
                      weekView={selectedWeekView}
                      accessLevel={String(modalData.accessLevel || '').trim()}
                      showCurrentSlotHighlight={showCurrentSlotHighlight}
                      currentDayCode={String(modalData.currentDayCode || '').trim()}
                    />
                  </>
                )}
              </>
            ) : (
              <p className='dashboardRotas_emptyText'>No rota data is available.</p>
            )}
          </div>
        </div>
      )}

    </>
  );
}
